/**
 * One photo shrink at a time, across the whole page.
 *
 * Shrinking a phone photo (downscaleImage in offerings.ts) decodes the WHOLE
 * original into memory before it draws the small copy: a 12 megapixel shot
 * is about 48 MB of pixels, a 24 megapixel one about 96 MB. Until 2026-10-04
 * every composer uploaded one file at a time, so only one decode was ever
 * alive. Photos now upload together (MessageAttachmentComposer.tsx,
 * MESSAGE_PHOTO_UPLOAD_CONCURRENCY), and three decodes at once on an older
 * phone is the kind of memory spike that gets a browser tab killed, taking
 * the half-written message with it. So the shrinks take turns while the
 * uploads they feed still overlap: photo two shrinks while photo one is on
 * the wire, which is where the time goes anyway.
 *
 * A turn never waits forever. If the shrink ahead of it has not finished in
 * PHOTO_SHRINK_TURN_MAX_WAIT_MS (a decode that never calls back, on some
 * browser we have not met), the next one goes anyway: two decodes at once is
 * a memory risk, a send that never starts is a broken composer. The clock
 * starts once the turn ahead has started, or when this one was asked for if
 * that is later, so a stuck first shrink lets the second go at 20 s and the
 * third at 40 s, never both at once (Codex review of #1333). Several stuck
 * decodes in a row therefore push later ones back 20 s each.
 *
 * Imports nothing, so it is pinned by a plain Node test
 * (tests/photo-shrink-turn.test.mjs).
 */

/** How long a shrink waits for the one ahead of it before going anyway. */
export const PHOTO_SHRINK_TURN_MAX_WAIT_MS = 20_000;

type Turn = { started: Promise<void>; done: Promise<void> };

let last: Turn = { started: Promise.resolve(), done: Promise.resolve() };

/**
 * Run `work` once every shrink queued before it has finished (or the one
 * just ahead has started and PHOTO_SHRINK_TURN_MAX_WAIT_MS has passed since
 * then and since this call). Resolves
 * or rejects exactly as `work` does, and a failed shrink still hands the
 * turn on.
 */
export async function inPhotoShrinkTurn<T>(work: () => Promise<T>): Promise<T> {
  const ahead = last;
  let markStarted: () => void = () => undefined;
  let handOn: () => void = () => undefined;
  last = {
    started: new Promise<void>((resolve) => { markStarted = resolve; }),
    done: new Promise<void>((resolve) => { handOn = resolve; }),
  };
  await ahead.started;
  await new Promise<void>((resolve) => {
    const timer = globalThis.setTimeout(resolve, PHOTO_SHRINK_TURN_MAX_WAIT_MS);
    void ahead.done.then(() => {
      globalThis.clearTimeout(timer);
      resolve();
    });
  });
  markStarted();
  try {
    return await work();
  } finally {
    handOn();
  }
}
